/**
 * Session ID management for user journey tracking
 * PRD-ADM-JOURNEY-001 Task 1.2
 *
 * Provides:
 * - Session ID generation and persistence (30-minute timeout)
 * - Request ID generation for tracing
 * - Activity tracking to extend session lifetime
 *
 * @canon CANON.md section 4 - RPC-only architecture
 */

const SESSION_STORAGE_KEY = 'supabank_session_id';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

let cachedSessionId: string | null = null;
let lastActivityTime: number = Date.now();

/**
 * Gets the current session ID, generating a new one if:
 * - No session exists
 * - Session has expired (30 minutes of inactivity)
 *
 * Session ID format: sess_<uuid>
 * Persisted in sessionStorage for tab lifetime
 *
 * @returns {string} Current session ID
 */
export function getSessionId(): string {
  // Check if session expired
  const now = Date.now();
  if (now - lastActivityTime > SESSION_TIMEOUT_MS) {
    cachedSessionId = null;
  }

  lastActivityTime = now;

  if (cachedSessionId) {
    return cachedSessionId;
  }

  // Try to restore from sessionStorage
  if (typeof window !== 'undefined') {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      cachedSessionId = stored;
      return stored;
    }
  }

  // Generate new session ID
  const newSessionId = `sess_${crypto.randomUUID()}`;

  if (typeof window !== 'undefined') {
    sessionStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
  }

  cachedSessionId = newSessionId;
  return newSessionId;
}

/**
 * Generates a unique request ID for distributed tracing
 *
 * Format: req_<timestamp>_<random>
 * Each request gets a unique ID for correlation across services
 *
 * @returns {string} Unique request ID
 */
export function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Alias for backwards compatibility
export const getRequestId = generateRequestId;

// Update session activity on user interaction
// This extends session lifetime when user is active
if (typeof window !== 'undefined') {
  ['click', 'keydown', 'scroll'].forEach(event => {
    window.addEventListener(event, () => {
      lastActivityTime = Date.now();
    }, { passive: true });
  });
}
